import { useEffect, useState } from 'react';

import { useIsSSR } from '@infinex/ui-utils/react-dom';

/**
 * A hook that returns whether the document matches a given media query.
 *
 * @param query - The [media query](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_media_queries/Using_media_queries) to match.
 */
export function useMediaQuery(query: string): boolean {
  const supportsMatchMedia =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function';

  const [matches, setMatches] = useState(() =>
    supportsMatchMedia ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    if (!supportsMatchMedia) {
      return;
    }

    const mq = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    mq.addEventListener('change', onChange);
    return () => {
      mq.removeEventListener('change', onChange);
    };
  }, [supportsMatchMedia, query]);

  // Within SSR environments, the media query should never match. Once the page
  // hydrates, this will update and the real value will be returned.
  return useIsSSR() ? false : matches;
}
